import { kv } from '@/services/platform';
import type { ListDetail, ListSummary } from '@/types/domain';

// Локальное зеркало read-model + outbox. Сейчас поверх kv (Preferences/localStorage),
// единый async-интерфейс — нативный @capacitor-community/sqlite станет drop-in адаптером.

export type OutboxKind =
  | 'list.create'
  | 'list.update'
  | 'list.delete'
  | 'list.checkAll'
  | 'item.create'
  | 'item.update'
  | 'item.delete'
  | 'item.check'
  | 'item.move';

export interface OutboxOp {
  /** op_id — идемпотентность очереди */
  id: string;
  kind: OutboxKind;
  /** локальный id затронутой сущности (может быть temp_ до синка) */
  localId: string;
  /** для create — родительский список (item) */
  listLocalId?: string;
  payload: Record<string, unknown>;
  createdAt: number;
  retries: number;
}

const K = {
  summaries: 'td.tasks.summaries',
  detail: (id: string) => `td.tasks.detail.${id}`,
  detailIndex: 'td.tasks.detailIndex',
  outbox: 'td.sync.outbox',
  cursor: 'td.sync.cursor',
};

// сериализация доступа к outbox: цепочка промисов (single-thread JS + await-точки → нужен мьютекс)
let outboxLock: Promise<unknown> = Promise.resolve();
function withOutboxLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = outboxLock.then(fn, fn);
  outboxLock = run.catch(() => undefined);
  return run;
}

export const localDb = {
  // ---- index (summaries) ----
  async getSummaries(): Promise<ListSummary[]> {
    return (await kv.getJson<ListSummary[]>(K.summaries)) ?? [];
  },
  async setSummaries(list: ListSummary[]): Promise<void> {
    await kv.setJson(K.summaries, list);
  },

  // ---- detail (дерево пунктов конкретного списка) ----
  async getDetail(id: string): Promise<ListDetail | null> {
    return kv.getJson<ListDetail>(K.detail(id));
  },
  async setDetail(detail: ListDetail): Promise<void> {
    await kv.setJson(K.detail(detail.id), detail);
    const idx = (await kv.getJson<string[]>(K.detailIndex)) ?? [];
    if (!idx.includes(detail.id)) {
      idx.push(detail.id);
      await kv.setJson(K.detailIndex, idx);
    }
  },
  async removeDetail(id: string): Promise<void> {
    await kv.remove(K.detail(id));
    const idx = (await kv.getJson<string[]>(K.detailIndex)) ?? [];
    await kv.setJson(
      K.detailIndex,
      idx.filter((x) => x !== id),
    );
  },

  // ---- outbox (все мутации сериализованы через outboxLock — без потери ops при гонке) ----
  async getOutbox(): Promise<OutboxOp[]> {
    return (await kv.getJson<OutboxOp[]>(K.outbox)) ?? [];
  },
  /** Атомарно добавить op в хвост. */
  async appendOutboxOp(op: OutboxOp): Promise<void> {
    await withOutboxLock(async () => {
      const ops = (await kv.getJson<OutboxOp[]>(K.outbox)) ?? [];
      ops.push(op);
      await kv.setJson(K.outbox, ops);
    });
  },
  /** Атомарно удалить op по id (безопасно при изменившемся порядке). */
  async removeOutboxOp(id: string): Promise<void> {
    await withOutboxLock(async () => {
      const ops = (await kv.getJson<OutboxOp[]>(K.outbox)) ?? [];
      await kv.setJson(
        K.outbox,
        ops.filter((o) => o.id !== id),
      );
    });
  },
  /** Атомарно переписать temp→real id во всех оставшихся ops (переживает рестарт). */
  async remapOutboxIds(fromId: string, toId: string): Promise<void> {
    await withOutboxLock(async () => {
      const ops = (await kv.getJson<OutboxOp[]>(K.outbox)) ?? [];
      for (const o of ops) {
        if (o.localId === fromId) o.localId = toId;
        if (o.listLocalId === fromId) o.listLocalId = toId;
        if (o.payload.parentId === fromId) o.payload.parentId = toId;
        if (o.payload.afterItemId === fromId) o.payload.afterItemId = toId;
      }
      await kv.setJson(K.outbox, ops);
    });
  },

  // ---- sync cursor (ChangeLog.seq, задел под P4 delta-pull) ----
  async getCursor(): Promise<number> {
    return (await kv.getJson<number>(K.cursor)) ?? 0;
  },
  async setCursor(seq: number): Promise<void> {
    await kv.setJson(K.cursor, seq);
  },

  async clearAll(): Promise<void> {
    const idx = (await kv.getJson<string[]>(K.detailIndex)) ?? [];
    await Promise.all(idx.map((id) => kv.remove(K.detail(id))));
    await Promise.all([
      kv.remove(K.summaries),
      kv.remove(K.detailIndex),
      kv.remove(K.outbox),
      kv.remove(K.cursor),
    ]);
  },
};
